<template>
    <div class="page-contents">
        <component
            :is="globalStore.header.component"
            v-if="globalStore && globalStore.header"
            :global="globalStore.global.content"
            :blok="globalStore.header"
        />
        <main>
            <component :is="story.content.component" v-if="story" :blok="story.content" />
        </main>
        <Component
            :is="'Lazy' + globalStore.emailSubscription.component"
            v-if="globalStore && globalStore.emailSubscription"
            :global="globalStore.global.content"
            :blok="globalStore.emailSubscription"
        />
        <Component
            :is="'Lazy' + globalStore.footer.component"
            v-if="globalStore && globalStore.footer"
            :global="globalStore.global.content"
            :blok="globalStore.footer"
        />
    </div>
</template>

<script setup lang="ts">
// Constant variable for access to quote store
import { useGlobalStore } from '../store/global';

const globalStore: any = useGlobalStore();
const { locale } = useI18n();
const route: any = useRoute();

// Constant variable for access to Runtime Config
const { STORYBLOK_FOLDER, STORYBLOK_VERSION, STORYBLOK_ROUTE_BLOCKED }: any = useRuntimeConfig().public;

// Need to resolve shared components to allow the storyblok API
// to expose this for us to use.
const resolveRelations: string[] = [
    'Page.globals',
    'CoreLocationPage.globals',
    'ImageGridAirports.Airports',
    'ImageGridAirportsV2.Airports',
    'ImageGrid.Airports',
    'Link_Section.entities',
    'PSFAirportPage.globals',
    'PSFAirportPage.AirportEntity',
    'PartnersContainer.Partners',
    'LinkListGroup.entities',
    'SPSAirportPage.globals',
    'SPSAirportPage.AirportEntity',
];

// JetParks: CoreLocationPage.entity must be resolved
if (STORYBLOK_FOLDER === 'jp') {
    resolveRelations.push('CoreLocationPage.entity');
}

const isInVisualEditor = !!route?.query?.['_storyblok_tk[token]']; //Check if frontend is opened in visual editor
const sbLocation = `${STORYBLOK_FOLDER}/pages/${route.params && route.params.slug && route.params.slug.length > 0 ? route.params.slug.join('/') : 'home'}`;
// Perform request to Storyblok to retrieve content
const data: any = await useAsyncStoryblok(sbLocation, {
    // Enable deep reactivity to fix live editing when in visual editor.
    // Because of changes for useAsyncData in Nuxt4 https://github.com/storyblok/monoblok/issues/210#issuecomment-3172675601
    deep: isInVisualEditor,
    api: {
        version: STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
        language: locale.value,
        resolve_relations: resolveRelations,
    },
    bridge: {
        resolveRelations: resolveRelations,
    },
});

const story = data?.story;
const error = data?.error;

if (story) {
    let config: { STORYBLOK_VERSION: string; STORYBLOK_FOLDER: string } = {
        STORYBLOK_VERSION: STORYBLOK_VERSION,
        STORYBLOK_FOLDER: STORYBLOK_FOLDER,
    };
    setSeoMeta(story.value?.content?.seo, config, route);
    if (story.value?.content?.globals) {
        globalStore.update(story.value.content.globals);
    }
}

function getStoryblokBlockedRoutes() {
    if (STORYBLOK_ROUTE_BLOCKED) {
        const blocked = STORYBLOK_ROUTE_BLOCKED.split(', ');
        return blocked.some((block) => route.path.includes(block));
    } else {
        return false;
    }
}

// Send user to error page if no story found
if (getStoryblokBlockedRoutes()) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

if (error?.value) {
    throw createError({
        statusCode: error.value.statusCode ?? 500,
        statusMessage: error.value.statusMessage ?? 'Failed to load page',
        fatal: true,
    });
}

onMounted(() => {
    // Allow use of Storyblok Bridge (Visual Editor)
    useStoryblokBridge(story.id, (evStory: any) => (story.value = evStory), {
        resolveRelations,
    });
});
</script>
